// Adapter que difiere la carga del SDK real a un chunk aparte.
//
// Sin esto, posthog-js (≈52 kB gzip) entra en el bundle de entrada y se descarga
// en /login —antes de que el usuario se autentique, y aun cuando analytics está
// apagado por falta de key—. Con esto sólo se descarga cuando el adapter real se
// va a usar de verdad.
//
// Las llamadas que ocurren mientras el chunk viaja se encolan y se reproducen en
// orden. Es importante para `register`: el core llama a register durante init,
// o sea siempre antes de que el SDK esté disponible.
import type { AnalyticsAdapter } from "../types";

/** Tope de la cola. El chunk es local: en la práctica resuelve en milisegundos. */
const MAX_PENDING = 50;

export function createLazyAdapter(
  name: string,
  load: () => Promise<AnalyticsAdapter>
): AnalyticsAdapter {
  let real: AnalyticsAdapter | null = null;
  let pending: Array<(adapter: AnalyticsAdapter) => void> = [];
  let failed = false;

  const run = (fn: (adapter: AnalyticsAdapter) => void): void => {
    if (real) {
      fn(real);
      return;
    }
    if (failed) return;
    if (pending.length >= MAX_PENDING) pending.shift();
    pending.push(fn);
  };

  return {
    name,

    init() {
      load()
        .then((adapter) => {
          adapter.init();
          real = adapter;
          const queued = pending;
          pending = [];
          queued.forEach((fn) => fn(adapter));
        })
        .catch(() => {
          // Si el chunk no carga (red caída, deploy a medias), analytics queda
          // inerte. Nunca se propaga el error a la app.
          failed = true;
          pending = [];
        });
    },

    track: (event, props, opts) => run((adapter) => adapter.track(event, props, opts)),
    page: (screen, props, opts) => run((adapter) => adapter.page(screen, props, opts)),
    identify: (id, traits) => run((adapter) => adapter.identify(id, traits)),
    register: (props) => run((adapter) => adapter.register(props)),
    reset: () => run((adapter) => adapter.reset()),
  };
}
